import type { CampaignAdProduct } from '@/types/spCampaigns';
import type {
  AdGroupNode,
  CampaignNode,
  EntityCrossFilter,
  EntityGraph,
  FilteredEntities,
  KeywordDisplayRow,
  PortfolioNode,
  WorkspaceResolutionContext,
} from './types';
import { campaignKeyForAdGroup } from './buildEntityGraph';
import { resolveWorkspaceConstraints } from './resolveWorkspaceConstraints';
import { hasOnlyFacetPins, isCrossFilterEmpty } from './toggleFilter';

export type WorkspaceColumnFilters = {
  portfolioSearch: string;
  portfolioState: 'ALL' | 'ENABLED' | 'PAUSED';
  campaignAdProduct: 'ALL' | CampaignAdProduct;
  campaignStatus: 'ALL' | 'ACTIVE' | 'PAUSED' | 'ARCHIVED';
  campaignSearch: string;
  adGroupAdProduct: 'ALL' | CampaignAdProduct;
  adGroupState: 'ALL' | 'ENABLED' | 'PAUSED' | 'ARCHIVED';
  adGroupCampaignState: 'ALL' | 'ENABLED' | 'PAUSED' | 'ARCHIVED';
  adGroupSearch: string;
  keywordState: 'ALL' | 'ENABLED' | 'PAUSED' | 'ARCHIVED';
  keywordMatchType: 'ALL' | 'EXACT' | 'PHRASE' | 'BROAD';
  keywordSearch: string;
};

export const defaultWorkspaceColumnFilters = (): WorkspaceColumnFilters => ({
  portfolioSearch: '',
  portfolioState: 'ENABLED',
  campaignAdProduct: 'ALL',
  campaignStatus: 'ACTIVE',
  campaignSearch: '',
  adGroupAdProduct: 'ALL',
  adGroupState: 'ENABLED',
  adGroupCampaignState: 'ENABLED',
  adGroupSearch: '',
  keywordState: 'ENABLED',
  keywordMatchType: 'ALL',
  keywordSearch: '',
});

function matchesSearch(haystack: string, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return haystack.toLowerCase().includes(q);
}

function campaignStatusMatches(state: string, filter: WorkspaceColumnFilters['campaignStatus']): boolean {
  if (filter === 'ALL') return true;
  const normalized = state.toUpperCase();
  if (filter === 'ACTIVE') return normalized === 'ENABLED';
  return normalized === filter;
}

function parentCampaignStateMatches(
  graph: EntityGraph,
  row: AdGroupNode,
  filter: WorkspaceColumnFilters['adGroupCampaignState']
): boolean {
  if (filter === 'ALL') return true;
  const parent = graph.indices.campaignsByKey.get(campaignKeyForAdGroup(row.adProduct, row.campaignId));
  if (!parent) return false;
  return parent.state.toUpperCase() === filter;
}

export function applyColumnFilters(
  graph: EntityGraph,
  columnFilters: WorkspaceColumnFilters
): Pick<FilteredEntities, 'portfolios' | 'campaigns' | 'adGroups' | 'keywords'> {
  const portfolios = graph.portfolios.filter((row) => {
    if (columnFilters.portfolioState !== 'ALL' && row.state.toUpperCase() !== columnFilters.portfolioState) {
      return false;
    }
    return matchesSearch([row.name, row.portfolioId, row.state].join(' '), columnFilters.portfolioSearch);
  });

  const campaigns = graph.campaigns.filter((row) => {
    if (columnFilters.campaignAdProduct !== 'ALL' && row.adProduct !== columnFilters.campaignAdProduct) {
      return false;
    }
    if (!campaignStatusMatches(row.state, columnFilters.campaignStatus)) return false;
    return matchesSearch(
      [row.name, row.campaignId, row.campaignKey, row.adProduct].join(' '),
      columnFilters.campaignSearch
    );
  });

  const adGroups = graph.adGroups.filter((row) => {
    if (columnFilters.adGroupAdProduct !== 'ALL' && row.adProduct !== columnFilters.adGroupAdProduct) {
      return false;
    }
    if (columnFilters.adGroupState !== 'ALL' && row.state.toUpperCase() !== columnFilters.adGroupState) {
      return false;
    }
    if (!parentCampaignStateMatches(graph, row, columnFilters.adGroupCampaignState)) {
      return false;
    }
    return matchesSearch(
      [row.name, row.adGroupId, row.campaignId, row.adProduct].join(' '),
      columnFilters.adGroupSearch
    );
  });

  const keywords = graph.keywords.filter((row) => {
    if (columnFilters.keywordState !== 'ALL') {
      const states = row.sources.map((s) => String(s.state ?? '').toUpperCase());
      if (!states.includes(columnFilters.keywordState)) return false;
    }
    if (columnFilters.keywordMatchType !== 'ALL') {
      if (row.match_type.toUpperCase() !== columnFilters.keywordMatchType) return false;
    }
    return matchesSearch(
      [row.keyword_text, row.match_type, row.campaign_types].join(' '),
      columnFilters.keywordSearch
    );
  });

  return { portfolios, campaigns, adGroups, keywords };
}

function keywordRowMatches(keywordIds: Set<string>, row: KeywordDisplayRow, graph: EntityGraph): boolean {
  const ids = graph.indices.keywordIdsByGroupKey.get(row.groupKey) ?? [];
  return ids.some((id) => keywordIds.has(id));
}

export function applyEntityCrossFilter(
  graph: EntityGraph,
  columnFiltered: Pick<FilteredEntities, 'portfolios' | 'campaigns' | 'adGroups' | 'keywords'>,
  filter: EntityCrossFilter,
  resolutionCtx: WorkspaceResolutionContext = {}
): FilteredEntities {
  if (isCrossFilterEmpty(filter) || hasOnlyFacetPins(filter)) {
    return { ...columnFiltered, conflict: false };
  }

  const constraints = resolveWorkspaceConstraints(graph, filter, resolutionCtx);
  if (constraints.conflict) {
    return { portfolios: [], campaigns: [], adGroups: [], keywords: [], conflict: true };
  }

  const portfolios = columnFiltered.portfolios.filter((row: PortfolioNode) =>
    constraints.portfolioIds.has(row.portfolioId)
  );
  const campaigns = columnFiltered.campaigns.filter((row: CampaignNode) =>
    constraints.campaignKeys.has(row.campaignKey)
  );
  const adGroups = columnFiltered.adGroups.filter((row: AdGroupNode) =>
    constraints.adGroupIds.has(row.adGroupId)
  );
  const keywords = columnFiltered.keywords.filter((row) =>
    keywordRowMatches(constraints.keywordIds, row, graph)
  );

  return { portfolios, campaigns, adGroups, keywords, conflict: false };
}

export function filterWorkspaceEntities(
  graph: EntityGraph,
  filter: EntityCrossFilter,
  columnFilters: WorkspaceColumnFilters,
  resolutionCtx: WorkspaceResolutionContext = {}
): FilteredEntities {
  const columnFiltered = applyColumnFilters(graph, columnFilters);
  return applyEntityCrossFilter(graph, columnFiltered, filter, resolutionCtx);
}
